import { useState, useEffect, lazy, Suspense, type ComponentType } from 'react';
import { useParams, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import type { ArcheOpenedSource } from '@fias/analytics-types';
import { analytics } from '@/lib/analytics';
import { ArcheLayout, ArcheDisplayMode } from '../components/ArcheLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { PluginRenderer } from '../components/PluginRenderer';
import { ArcheLoadingScreen } from '../components/ArcheLoadingScreen';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { ArcheMetadata } from '@/lib/api';

/**
 * Lazy-loaded arche page components.
 *
 * Each entry is its own dynamic-import boundary, so Rollup emits a
 * separate chunk per arche and the browser only downloads the code
 * for arches the user actually opens. Combined with vite.config.ts
 * `manualChunks` (which carves out the heaviest vendor libs into
 * named cacheable chunks), this is what stops the shell bundle from
 * growing linearly with arche count.
 *
 * Order is alphabetical-by-archeId for ease of audit. Adding a new
 * arche means adding ONE line here plus ONE line in the registry
 * below — there is no other touch point.
 */
const BankStatementDownloadArchePage = lazy(() =>
  import('./bank-statement-download').then((m) => ({ default: m.BankStatementDownloadArchePage })),
);
const BetaAccessArchePage = lazy(() =>
  import('./beta-access').then((m) => ({ default: m.BetaAccessArchePage })),
);
const BusinessArchePage = lazy(() =>
  import('./businesses').then((m) => ({ default: m.BusinessArchePage })),
);
const CalendarArchePage = lazy(() =>
  import('./calendar').then((m) => ({ default: m.CalendarArchePage })),
);
const ChatArchePage = lazy(() => import('./chat').then((m) => ({ default: m.ChatArchePage })));
const ContactBookArchePage = lazy(() =>
  import('./contact-book').then((m) => ({ default: m.ContactBookArchePage })),
);
const CraftingArchePage = lazy(() =>
  import('./crafting').then((m) => ({ default: m.CraftingArchePage })),
);
const CustomerSupportArchePage = lazy(() =>
  import('./customer-support').then((m) => ({ default: m.CustomerSupportArchePage })),
);
const DfeArchePage = lazy(() => import('./dfe').then((m) => ({ default: m.DfeArchePage })));
const DfeAvatarsArchePage = lazy(() =>
  import('./dfe-avatars').then((m) => ({ default: m.DfeAvatarsArchePage })),
);
const DfeGameStateArchePage = lazy(() =>
  import('./dfe-game-state').then((m) => ({ default: m.DfeGameStateArchePage })),
);
const DfeIconsArchePage = lazy(() =>
  import('./dfe-icons').then((m) => ({ default: m.DfeIconsArchePage })),
);
const DfeInventoryTransitionArchePage = lazy(() =>
  import('./dfe-inventory-transition').then((m) => ({
    default: m.DfeInventoryTransitionArchePage,
  })),
);
const DocumentSignArchePage = lazy(() =>
  import('./document-sign').then((m) => ({ default: m.DocumentSignArchePage })),
);
const EmailArchePage = lazy(() => import('./email').then((m) => ({ default: m.EmailArchePage })));
const FavoritesArchePage = lazy(() =>
  import('./favorites').then((m) => ({ default: m.FavoritesArchePage })),
);
const Fias360ArchePage = lazy(() =>
  import('./fias360').then((m) => ({ default: m.Fias360ArchePage })),
);
const FiasLeadsArchePage = lazy(() =>
  import('./fias-leads').then((m) => ({ default: m.FiasLeadsArchePage })),
);
const GameRewardsArchePage = lazy(() =>
  import('./game-rewards').then((m) => ({ default: m.GameRewardsArchePage })),
);
const GuildArchePage = lazy(() => import('./guilds').then((m) => ({ default: m.GuildArchePage })));
const IconGeneratorArchePage = lazy(() =>
  import('./icon-generator').then((m) => ({ default: m.IconGeneratorArchePage })),
);
const ImageEditorArchePage = lazy(() =>
  import('./image-editor').then((m) => ({ default: m.ImageEditorArchePage })),
);
const ImageGeneratorArchePage = lazy(() =>
  import('./image-generator').then((m) => ({ default: m.ImageGeneratorArchePage })),
);
const LearnAboutFiasArchePage = lazy(() =>
  import('./learn-about-fias').then((m) => ({ default: m.LearnAboutFiasArchePage })),
);
const LearnAboutFiasChatPage = lazy(() =>
  import('./learn-fias-chat').then((m) => ({ default: m.LearnAboutFiasChatPage })),
);
const LearnAnythingPage = lazy(() =>
  import('./learn-anything').then((m) => ({ default: m.LearnAnythingPage })),
);
const LiquidityCachesArchePage = lazy(() =>
  import('./liquidity-caches').then((m) => ({ default: m.LiquidityCachesArchePage })),
);
const LitcraftAddressablesArchePage = lazy(() =>
  import('./litcraft-addressables').then((m) => ({ default: m.LitcraftAddressablesArchePage })),
);
const LitLegionArchePage = lazy(() =>
  import('./lit-legion').then((m) => ({ default: m.LitLegionArchePage })),
);
const LitSquadArchePage = lazy(() =>
  import('./litsquad').then((m) => ({ default: m.LitSquadArchePage })),
);
const MakeARequestPage = lazy(() =>
  import('./make-a-request').then((m) => ({ default: m.MakeARequestPage })),
);
const MiningArchePage = lazy(() =>
  import('./mining').then((m) => ({ default: m.MiningArchePage })),
);
const MultiAIPage = lazy(() => import('./multi-ai').then((m) => ({ default: m.MultiAIPage })));
const NotesArchePage = lazy(() => import('./notes').then((m) => ({ default: m.NotesArchePage })));
const PluginBuilderArchePage = lazy(() =>
  import('./plugin-builder').then((m) => ({ default: m.PluginBuilderArchePage })),
);
const PollsArchePage = lazy(() => import('./polls').then((m) => ({ default: m.PollsArchePage })));
const SingleAIPage = lazy(() => import('./single-ai').then((m) => ({ default: m.SingleAIPage })));
const SocialMediaArchePage = lazy(() =>
  import('./social-media').then((m) => ({ default: m.SocialMediaArchePage })),
);
const TodoListsArchePage = lazy(() =>
  import('./todo-lists').then((m) => ({ default: m.TodoListsArchePage })),
);
const TravelArchePage = lazy(() =>
  import('./travel').then((m) => ({ default: m.TravelArchePage })),
);
const UploadBankingArchePage = lazy(() =>
  import('./upload-banking').then((m) => ({ default: m.UploadBankingArchePage })),
);
const VaultDocumentsArchePage = lazy(() =>
  import('./vault-documents').then((m) => ({ default: m.VaultDocumentsArchePage })),
);
const VoiceStudioArchePage = lazy(() =>
  import('./voice-studio').then((m) => ({ default: m.VoiceStudioArchePage })),
);

/** Suspense fallback for lazy-loaded arche pages. */
function ArcheLoadingFallback() {
  return <ArcheLoadingScreen />;
}

/**
 * Determines the display mode for the arche layout based on metadata, auth state, and embed flag.
 *
 * Security: Embed mode is only allowed for arches that are:
 * - Public (isPublic=true)
 * - Read-only (no workflow)
 * This prevents authenticated actions from being performed in embedded iframes.
 */
function determineDisplayMode(
  metadata: ArcheMetadata | null,
  isAuthenticated: boolean,
  isEmbed: boolean,
): ArcheDisplayMode {
  // Embed mode is only allowed for embeddable arches (public + no workflow)
  // This is a security measure to prevent authenticated actions in iframes
  if (isEmbed && metadata?.isEmbeddable) {
    return 'embed';
  }

  if (!metadata) {
    return 'normal'; // Default to normal while loading
  }

  // Full-screen mode takes precedence
  if (metadata.isFullScreen) {
    return 'fullscreen';
  }

  // Public mode for unauthenticated users on public arches
  if (metadata.isPublic && !isAuthenticated) {
    return 'public';
  }

  return 'normal';
}

/**
 * Error component shown when embed mode is requested but not allowed
 */
function EmbedNotAllowedError() {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <Card className="max-w-md mx-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Embedding Not Allowed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            This arche cannot be embedded. Only public, read-only arches can be embedded in iframes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Generic arche page for dynamic arches (plugins, etc.)
 */
function GenericArchePage({ archeId }: { archeId: string }) {
  const { isAuthenticated, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [metadata, setMetadata] = useState<ArcheMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(true);

  // Check for embed mode via query parameter
  const isEmbed = searchParams.get('embed') === 'true';

  // Security: Clear any existing authentication when in embed mode
  // This prevents authenticated actions from being performed in embedded iframes
  useEffect(() => {
    if (isEmbed && isAuthenticated) {
      signOut();
    }
  }, [isEmbed, isAuthenticated, signOut]);

  useEffect(() => {
    let cancelled = false;

    async function fetchMetadata() {
      try {
        const result = await api.getArcheMetadata(archeId);
        if (!cancelled) {
          setMetadata(result);
        }
      } catch {
        if (!cancelled) {
          setMetadata(null);
        }
      } finally {
        if (!cancelled) {
          setMetadataLoading(false);
        }
      }
    }

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, [archeId]);

  // Check if embed mode was requested but not allowed
  const embedDenied = isEmbed && !metadataLoading && metadata && !metadata.isEmbeddable;

  if (embedDenied) {
    return <EmbedNotAllowedError />;
  }

  // Plugin arches render in a sandboxed iframe
  // Use metadata.archId (the resolved real ID) instead of the URL param which may be a slug
  if (!metadataLoading && metadata?.renderMode === 'plugin') {
    const displayMode = determineDisplayMode(metadata, isAuthenticated, isEmbed);
    return (
      <ArcheLayout displayMode={displayMode}>
        <PluginRenderer archeId={metadata.archId} />
      </ArcheLayout>
    );
  }

  // Non-plugin generic arches — show "not available"
  if (!metadataLoading) {
    return (
      <ArcheLayout displayMode="normal">
        <div className="p-8 max-w-3xl mx-auto">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Not Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">This arche is not available.</p>
            </CardContent>
          </Card>
        </div>
      </ArcheLayout>
    );
  }

  // metadataLoading — render the loading screen inside the standard layout so
  // the sidebar/header don't pop in after metadata resolves. In embed mode we
  // skip the chrome since embeds are intentionally chromeless.
  if (isEmbed) {
    return <ArcheLoadingScreen />;
  }
  return (
    <ArcheLayout displayMode="normal">
      <ArcheLoadingScreen />
    </ArcheLayout>
  );
}

function inferArcheOpenedSource(state: unknown, referrer: string): ArcheOpenedSource {
  // react-router-dom v7 carries an opaque `state` object on navigations.
  // We honor an explicit { archeOpenSource: ArcheOpenedSource } if present
  // so that future navigation call sites can attribute properly.
  if (state && typeof state === 'object' && 'archeOpenSource' in state) {
    const raw = (state as { archeOpenSource: unknown }).archeOpenSource;
    if (raw === 'directory' || raw === 'search' || raw === 'recommended' || raw === 'direct_link') {
      return raw;
    }
  }
  // Heuristic fallback: arrived from /marketplace or /arches → directory.
  // No referrer (typed URL or external link) → direct_link.
  try {
    const u = referrer ? new URL(referrer) : null;
    if (
      u &&
      (u.pathname === '/marketplace' ||
        u.pathname.startsWith('/marketplace/') ||
        u.pathname === '/arches')
    ) {
      return 'directory';
    }
  } catch {
    // ignore malformed referrers
  }
  return 'direct_link';
}

/**
 * Map of archeId → page component for internal arches with dedicated UIs.
 * Adding a new internal arche means adding an import above and one entry
 * here; no other edits to this file are needed.
 *
 * Plugin arches (and any unmapped ID) fall through to `GenericArchePage`,
 * which is the iframe-based renderer.
 *
 * Long-term, this registry is a candidate for code-splitting via
 * `React.lazy` per arche, and for being driven by the `arches` DB table
 * directly so adding an arche requires no edits here at all. See
 * docs/audits/social-media-modularity-audit.md §10 PW-C.
 */
const INTERNAL_ARCHE_COMPONENTS: Record<string, ComponentType> = {
  arche_guilds: GuildArchePage,
  arche_chat: ChatArchePage,
  arche_make_a_request: MakeARequestPage,
  arche_single_ai: SingleAIPage,
  arche_image_generator: ImageGeneratorArchePage,
  arche_multi_ai: MultiAIPage,
  arche_learn_fias_chat: LearnAboutFiasChatPage,
  arche_learn_anything: LearnAnythingPage,
  arche_dfe: DfeArchePage,
  arche_litsquad: LitSquadArchePage,
  arche_dfe_inventory_transition: DfeInventoryTransitionArchePage,
  arche_contact_book: ContactBookArchePage,
  arche_favorites: FavoritesArchePage,
  arche_game_rewards: GameRewardsArchePage,
  arche_dfe_game_state: DfeGameStateArchePage,
  arche_polls: PollsArchePage,
  arche_plugin_builder: PluginBuilderArchePage,
  arche_crafting: CraftingArchePage,
  arche_dfe_mining: MiningArchePage,
  arche_learn_about_fias: LearnAboutFiasArchePage,
  arche_liquidity_caches: LiquidityCachesArchePage,
  arche_calendar: CalendarArchePage,
  arche_social_media: SocialMediaArchePage,
  arche_icon_generator: IconGeneratorArchePage,
  arche_lit_legion: LitLegionArchePage,
  arche_businesses: BusinessArchePage,
  arche_image_editor: ImageEditorArchePage,
  arche_beta_access: BetaAccessArchePage,
  arche_customer_support: CustomerSupportArchePage,
  arche_dfe_avatars: DfeAvatarsArchePage,
  arche_dfe_icons: DfeIconsArchePage,
  arche_litcraft_addressables: LitcraftAddressablesArchePage,
  arche_todo_lists: TodoListsArchePage,
  arche_fias_leads: FiasLeadsArchePage,
  arche_bank_statement_download: BankStatementDownloadArchePage,
  // Consolidated from per-arche if-branches (May 2026 audit): every
  // archeId mapping belongs in this registry, not in a switch-tail
  // below the early-return. The registry is the source of truth.
  arche_vault_notes: NotesArchePage,
  arche_fias360: Fias360ArchePage,
  arche_vault_documents: VaultDocumentsArchePage,
  arche_voice_studio: VoiceStudioArchePage,
  arche_document_sign: DocumentSignArchePage,
  arche_email: EmailArchePage,
  arche_travel: TravelArchePage,
  arche_upload_banking: UploadBankingArchePage,
};

/**
 * Map of archeId → external URL for arches that are redirects to other
 * top-level routes rather than embedded components.
 */
const ARCHE_REDIRECTS: Record<string, string> = {
  arche_marketplace: '/marketplace',
};

export function ArcheInteractionPage() {
  const { id: archeId } = useParams<{ id: string }>();
  const location = useLocation();

  // Emit arche_opened on entry, arche_left on unmount/swap. Time-spent
  // inside is the primary dashboard signal for arche engagement.
  useEffect(() => {
    if (!archeId) return;
    const source = inferArcheOpenedSource(location.state, document.referrer);
    analytics.archeOpened(archeId, source);
    const enteredAt = Date.now();
    return () => {
      analytics.archeLeft(archeId, Date.now() - enteredAt);
    };
  }, [archeId, location.state]);

  if (!archeId) {
    return <Navigate to="/a/arche_make_a_request" replace />;
  }

  // Redirects to other top-level routes
  const redirectTo = ARCHE_REDIRECTS[archeId];
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  // Internal arches with dedicated page components. Lazy-loaded, so we
  // wrap in Suspense — first paint shows the spinner fallback while the
  // arche's chunk downloads.
  const Component = INTERNAL_ARCHE_COMPONENTS[archeId];
  if (Component) {
    return (
      <Suspense fallback={<ArcheLoadingFallback />}>
        <Component />
      </Suspense>
    );
  }

  // All other arches (plugins, etc.) use the generic iframe page
  return <GenericArchePage archeId={archeId} />;
}

export default ArcheInteractionPage;
